import React, {FC, useState, useEffect} from "react"

import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {H2, H3, H6, Body, Label} from "@irati/ui/src/Typography"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Card} from "@irati/ui/src/Layout"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {Tooltip, OverlayPlacement} from "@irati/ui/src/Tooltip"
import Validation from "@irati/utils/validation"
import ErrorText from "@irati/ui/src/ErrorText"
import Spacing from "@irati/ui/src/Spacing"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {RenewalProduct, ZuoraProductOptions} from "@irati/types"

import {AddOns} from "../../Renewal/Header"
import {Container, StyledHr, StyledIcon, StyledInput, CustomChip, FlexiPointsImg, EAPImg} from "./styled"
import ProductCard, {ProductOption} from "./ProductCard"
import {FLEXI_PRODUCT, FLEXI_POINTS_PREFIX, EAP_PRODUCT, MEDICAL_PRODUCT, NO_EAP} from "./constants"
import {FLEXI_POINTS_OPTIONS, EAP_OPTIONS} from "./productOptions"

const MIN_SEATS = 20
const MAX_SEATS = 50000
interface Props extends InjectIntl {
  seats: number
  originalSeats: number
  originalUkSeats: number
  renewalSeats: number
  addOns: AddOns[]
  onCallback: (seats: number | null, ukSeats?: number | null) => void
  onUpdateFlexi: () => void
  onUpdateEAP: () => void
  onError: () => void
  products: RenewalProduct[]
  productOptions: ZuoraProductOptions
  flexiUpgrade: RenewalProduct
  EAPUpgrade: RenewalProduct
  EAPSeats: number
}

const NewPlanCard: FC<Props> = ({
  seats,
  originalSeats,
  originalUkSeats,
  renewalSeats,
  addOns,
  products,
  productOptions,
  onUpdateFlexi,
  onUpdateEAP,
  onCallback,
  onError,
  flexiUpgrade,
  EAPUpgrade,
  EAPSeats,
  formatMessage
}) => {
  const initialFlexiPointsOptions = () => {
    const initialFlexiOption =
      products?.find((product) => product.name.includes(FLEXI_POINTS_PREFIX))?.name || FLEXI_POINTS_OPTIONS[0].value
    const otherFlexOptions = productOptions?.upgradeSequences.find(({name}) => name === FLEXI_PRODUCT)?.upgrades?.map(({name}) => name)

    const flexiPointOptionsToRender = FLEXI_POINTS_OPTIONS.filter((flexiOption) => {
      return flexiOption.value === initialFlexiOption || otherFlexOptions?.find((option) => option === flexiOption.value)
    })
      .slice(0, 4)
      .map((option, i) => {
        return {
          ...option,
          checked: flexiUpgrade ? option.value === flexiUpgrade?.name : i === 0,
          upgrade: productOptions?.upgradeSequences
            .find(({name}) => name === FLEXI_PRODUCT)
            ?.upgrades?.find(({name}) => name === option.value)
        }
      })
    return flexiPointOptionsToRender
  }

  const initialEAPOptions = () => {
    return EAP_OPTIONS.map((option, index) => {
      return {
        ...option,
        checked: option.value === EAPUpgrade?.name || (!EAPUpgrade && index === 0),
        upgrade: productOptions?.upgradeSequences.find(({name}) => name === EAP_PRODUCT)?.upgrades?.find(({name}) => name === option.value)
      }
    })
  }

  const EAP = products?.find((product) => product.name.includes(EAP_PRODUCT))
  const [seatsNo, setSeatsNo] = useState<number | null>(seats)
  const [ukSeats, setUkSeats] = useState<number | null>(EAPSeats || EAP?.seats)
  const [flexiPoints, setFlexiPoints] = useState<ProductOption[]>(initialFlexiPointsOptions())
  const [eap, setEap] = useState<ProductOption[]>(initialEAPOptions())
  const [minSeats, setMinSeats] = useState<number>(0)
  const [minUKSeats, setMinUKSeats] = useState<number>(0)
  const SEVENTY_PERCENT_OF_CURRENT_SEATS = 0.7

  const theme = useTheme()

  useEffect(() => {
    setSeatsNo(seats)
  }, [seats])

  useEffect(() => {
    const minimumSeats = Math.round(renewalSeats * SEVENTY_PERCENT_OF_CURRENT_SEATS)

    if (renewalSeats < MIN_SEATS) {
      setMinSeats(minimumSeats)
    } else if (minimumSeats < MIN_SEATS) {
      setMinSeats(MIN_SEATS)
    } else {
      setMinSeats(minimumSeats)
    }
    if (originalUkSeats) {
      const minimumUKSeats = Math.round(originalUkSeats * SEVENTY_PERCENT_OF_CURRENT_SEATS)
      if (originalUkSeats < MIN_SEATS) {
        setMinUKSeats(minimumUKSeats)
      } else if (minimumUKSeats < MIN_SEATS) {
        setMinUKSeats(MIN_SEATS)
      } else {
        setMinUKSeats(minimumUKSeats)
      }
    }
  }, [])

  const handleSeatsChange = (value) => {
    const newValue = parseInt(value, 10)
    setSeatsNo(value ? newValue : null)

    if (newValue! <= MAX_SEATS && newValue! >= minSeats) {
      if (!ukSeats) {
        onCallback(newValue, null)
      } else {
        setUkSeats(newValue)
        onCallback(newValue, newValue)
      }
    } else {
      onError()
    }
  }

  const handleUkSeatsChange = (value) => {
    const newValue = parseInt(value, 10)
    setUkSeats(value ? newValue : null)

    if (newValue! >= 0 && newValue! <= seatsNo! && newValue! <= MAX_SEATS && newValue! >= minUKSeats) {
      onCallback(seatsNo, newValue)
    } else if (!newValue) {
      onCallback(seatsNo, null)
    } else {
      onError()
    }
  }

  const handleFlexiPoints = (product: ProductOption) => {
    const flexiProduct = flexiPoints?.map((item) => {
      return {...item, checked: item.value === product.value}
    })
    setFlexiPoints(flexiProduct)
    onUpdateFlexi(product?.upgrade)
  }

  const handleEap = (product: ProductOption) => {
    const isPreviouslySelectedNoEAP = eap?.find(({checked}) => checked)?.name === NO_EAP
    const eapProduct = eap?.map((item) => {
      return {...item, checked: item.value === product.value}
    })
    const isCurrentlySelectedNonEAP = eapProduct?.find(({checked}) => checked)?.name === NO_EAP
    if (isPreviouslySelectedNoEAP) {
      handleUkSeatsChange(seatsNo)
    }
    if (isCurrentlySelectedNonEAP) {
      const medicalProduct = hasMedicalProduct()
      const revertedUKSeats = medicalProduct?.seats || 0
      handleUkSeatsChange(revertedUKSeats)
    }
    setEap(eapProduct)
    onUpdateEAP(product)
  }

  const hasEAPProduct = () => products?.find((product: RenewalProduct) => product.name.includes(EAP_PRODUCT))
  const hasMedicalProduct = () => products?.find((product: RenewalProduct) => product.name === MEDICAL_PRODUCT)
  const hasEAPUpgrades = () => productOptions?.upgradeSequences.find(({name}) => name === EAP_PRODUCT)?.upgrades.length

  const getLocalProductText = () => {
    if (hasMedicalProduct()) {
      if (hasEAPProduct()) {
        return "admin.billing.renewals.newPlanCards.eapMedicalTitle"
      }
      return "admin.billing.renewals.newPlanCards.medicalTitle"
    }
    return "admin.billing.renewals.newPlanCards.eapTitle"
  }
  const localProductText = getLocalProductText()
  const isEAPCurrentlySelected = !eap[0]?.checked

  return (
    <>
      <Flex marginBottom={3} marginTop={3}>
        <Label large color={theme.colors.greys.black} marginRight={3}>
          <FormattedMessage id="admin.billing.renewals.newPlan.currentPlanInfo" />
        </Label>
        <Flex wrap="wrap" gap={theme.spacing.one}>
          {addOns.map((data, i) => (
            <CustomChip key={i} fillColor={data?.icon === IconTypes.flexi ? theme.colors.primary2 : theme.colors.greys.white}>
              <Flex alignItems="center">
                <Icon color={theme.colors.primary2} icon={data?.icon} />
                <Spacing marginLeft>{formatMessage({id: data.label})}</Spacing>
              </Flex>
            </CustomChip>
          ))}
        </Flex>
      </Flex>
      {seatsNo < minSeats && <Alert type={AlertTypes.warning} title="admin.billing.renewals.newPlan.warningAlert.minSeats" />}
      {seatsNo < originalSeats && seatsNo >= minSeats && (
        <Alert type={AlertTypes.warning} title="admin.billing.renewals.newPlan.warningAlert.increaseSeats" />
      )}

      <Card padding={[0, 0]}>
        <H2 color={theme.colors.greys.black} padding={[3, 3]}>
          <FormattedMessage id="admin.billing.renewals.newPlanCards.title" />
        </H2>
        <Flex direction="column">
          <Container justifyContent="space-between" padding={[3, 3]}>
            <Flex>
              <Icon icon={IconTypes.userFilled} color={theme.colors.utility.ink} size={24} />
              <Flex direction="column" marginLeft>
                <H3 color={theme.colors.greys.black}>
                  <FormattedMessage id="admin.billing.renewals.newPlanCards.seats.title" />
                </H3>
                <Body small>
                  <FormattedMessage id="admin.billing.renewals.newPlanCards.seats.subTitle" values={{seats: originalSeats}} />
                  <Tooltip
                    tooltipChildren={
                      <Body small padding={[1, 1]}>
                        <FormattedMessage id="admin.billing.renewals.newPlanCards.seats.tooltip" />
                      </Body>
                    }
                    placement={OverlayPlacement.Bottom}>
                    <StyledIcon marginLeft icon={IconTypes.circleAlertFill} color={theme.colors.greys.dark} size={16} />
                  </Tooltip>
                </Body>
              </Flex>
            </Flex>
            <Flex direction="column">
              <Flex width="100%" justifyContent="flex-end">
                <StyledInput
                  hideLabel
                  style={{marginTop: 0, marginBottom: 5}}
                  value={seatsNo}
                  type="number"
                  name="renewalSeats"
                  validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"}))}
                  onChange={handleSeatsChange}
                  isError={seatsNo! < minSeats || seatsNo! > MAX_SEATS}
                />
              </Flex>
              {seatsNo! < minSeats && (
                <ErrorText>
                  <FormattedMessage id="admin.billing.renewals.newPlanCards.validatio.minLength" values={{count: minSeats}} />
                </ErrorText>
              )}
              {seatsNo! > MAX_SEATS && (
                <ErrorText>
                  <FormattedMessage id="admin.billing.renewals.newPlanCards.validatio.maxLength" values={{count: MAX_SEATS}} />
                </ErrorText>
              )}
            </Flex>
          </Container>
          <H6 paddingLeft={3} marginTop={3} color={theme.colors.greys.black}>
            <FormattedMessage id="admin.billing.renewals.newPlanCards.global" />
          </H6>
          <Flex justifyContent="space-between" padding={[3, 3]}>
            <Flex alignItems="flex-start">
              <Flex alignItems="center">
                <FlexiPointsImg />
                <H3 marginLeft color={theme.colors.greys.black}>
                  <FormattedMessage id="admin.billing.renewals.newPlanCards.flexi.title" />
                </H3>
              </Flex>
            </Flex>
            <Body small>
              <FormattedMessage id="admin.billing.renewals.newPlanCards.flexi.subTitle" />
            </Body>
          </Flex>
          <Flex justifyContent="flex-start" gap={theme.spacing.one} padding={[0, 3, 3]} marginLeft={6}>
            {flexiPoints?.map((item, i) => (
              <ProductCard key={i} product={item} onSelect={handleFlexiPoints} />
            ))}
          </Flex>
          {(hasMedicalProduct() || hasEAPProduct() || hasEAPUpgrades()) && (
            <>
              <StyledHr />
              <H6 paddingLeft={3} marginTop={3} marginBottom={3} color={theme.colors.greys.black}>
                <FormattedMessage id="admin.billing.renewals.newPlanCards.ukProducts" />
              </H6>
              <Container alignItems="center" justifyContent="space-between" padding={[4, 4]}>
                <Flex direction="column">
                  <Body small color={theme.colors.greys.black}>
                    <FormattedMessage id={localProductText} />
                  </Body>
                  {hasMedicalProduct() && !hasEAPProduct() && (
                    <Label color={theme.colors.greys.black}>
                      <FormattedMessage id="admin.billing.renewals.newPlanCards.medicalSubtitle" />
                    </Label>
                  )}
                </Flex>

                <Flex direction="column">
                  <Flex width="100%" justifyContent="flex-end">
                    <StyledInput
                      hideLabel
                      style={{marginTop: 0, marginBottom: 5}}
                      value={ukSeats}
                      type="number"
                      name="ukSeats"
                      placeholder={
                        EAPUpgrade || hasMedicalProduct()
                          ? formatMessage({id: "admin.billing.renewals.newPlanCards.validation.numbersOnly"})
                          : ""
                      }
                      onChange={handleUkSeatsChange}
                      readOnly={!EAPUpgrade && !hasMedicalProduct()}
                      isError={ukSeats! > seatsNo! || ukSeats! < 0 || ukSeats! < minUKSeats}
                    />
                  </Flex>
                  {ukSeats! > seatsNo! && isEAPCurrentlySelected && (
                    <ErrorText>
                      <FormattedMessage id="admin.billing.renewals.newPlanCards.validation.ukEmployees" />
                    </ErrorText>
                  )}
                  {ukSeats! < minUKSeats && isEAPCurrentlySelected && (
                    <ErrorText>
                      <FormattedMessage id="admin.billing.renewals.newPlanCards.validatio.minLength" values={{count: minUKSeats}} />
                    </ErrorText>
                  )}
                </Flex>
              </Container>
              <Flex justifyContent="space-between" padding={[3, 3]}>
                <Flex alignItems="flex-start">
                  <Flex alignItems="center">
                    <EAPImg />
                    <H3 marginLeft color={theme.colors.greys.black}>
                      <FormattedMessage id="admin.billing.renewals.newPlanCards.eap.title" />
                    </H3>
                  </Flex>
                </Flex>
                <Body small>
                  <FormattedMessage id="admin.billing.renewals.newPlanCards.eap.subTitle" />
                </Body>
              </Flex>
              <Flex justifyContent="flex-start" gap={theme.spacing.one} padding={[0, 3, 3]} marginLeft={6} marginBottom>
                {eap?.map((item, i) => (
                  <ProductCard key={i} product={item} onSelect={handleEap} />
                ))}
              </Flex>
            </>
          )}
        </Flex>
      </Card>
    </>
  )
}

export default injectIntl(NewPlanCard)
